import React, { createContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { message } from 'antd';
import { getBaseDomain } from '../../utils/domain';

// Helper to verify token
const verifyToken = async (token: string, url: string) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token })
  });
  if (!response.ok) {
    throw new Error('Token verification failed');
  }
  return response.json();
};

interface User {
  id: number;
  name: string;
  email: string;
  company_id: number | string;
  currency: string;
  userType: number;
  profile_image_url: string;
  logo_url: string;
  is_admin: boolean;
  is_superuser: boolean;
  countryid: number;
  company_name: string;
  company_base_currency: string;
  date_format: string;
  currency_symbol: string;
  timezone: string;
}

export interface UserContextType {
  userDetails: User | null;
  loading: boolean;
  fetchUserData: () => Promise<void>;
  decryptCompanyId: string;
}

const defaultContext: UserContextType = {
  userDetails: null,
  loading: true,
  fetchUserData: async () => {},
  decryptCompanyId: ''
};

const UserContext = createContext<UserContextType>(defaultContext);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userDetails, setUserDetails] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [decryptCompanyId, setDecryptCompanyId] = useState('');

  const baseDomain = getBaseDomain();
  const VERIFY_URL = `https://globalapi.${baseDomain}/api/v1/token/verify/`;
  const DECRYPT_URL = `https://globalapi.${baseDomain}/api/decrypt/`;
  const LOGIN_URL = `https://app.${baseDomain}/`;

  const getDecryptCompanyId = async (companyId: string) => {
    try {
      const res = await axios.post(DECRYPT_URL, { encrypt_id: companyId });
      if (res?.data?.status === 'success') {
        setDecryptCompanyId(res.data.decrypted_value);
        return res.data.decrypted_value;
      }
    } catch (error) {
      console.error(error);
    }
    return '';
  };

  const fetchUserData = async () => {
    setLoading(true);
    const userAuth = Cookies.get('accessToken');
    if (!userAuth) {
      Cookies.remove('accessToken');
      message.error('No access token available.');
      setLoading(false);
      window.location.href = LOGIN_URL;
      return;
    }

    try {
      const data = await verifyToken(userAuth, VERIFY_URL);
      if (data && data.company_id) {
        await getDecryptCompanyId(data.company_id);
      }
      const userData: User = {
        id: data?.user_id || 0,
        name: `${data?.firstname} ${data?.lastname}`,
        email: data?.email || '',
        profile_image_url: data?.profile_image || '',
        logo_url: data?.logo_url || '',
        company_id: data?.company_id || 0,
        is_admin: data?.is_admin || false,
        is_superuser: data?.is_superuser || false,
        countryid: data?.countryid || 0,
        userType: data?.FS_type || 0,
        currency: data?.currency,
        company_name: data?.company_name || '',
        company_base_currency: data?.company_base_currency || '',
        currency_symbol: data?.currency_symbol || '',
        date_format: data?.date_format || '',
        timezone: data?.timezone || ''
      };
      setUserDetails(userData);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserDetails(null);
      Cookies.remove('accessToken');
      window.location.href = LOGIN_URL;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  return (
    <UserContext.Provider value={{ userDetails, loading, fetchUserData, decryptCompanyId }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;

